// Stats — what's genuinely knowable browser-direct (no CF token through
// us). Three real sources: local publish history (OPFS-SQLite), the
// discovery indexer (CORS-open worker), and a register toggle. Raw CF
// page-views aren't reachable from the browser (CF API has no CORS, §2),
// so we link to the creator's own CF dashboard instead of faking numbers.

import { useCallback, useEffect, useState } from "react";
import { useAuthStore } from "../../store/authStore";
import { getOnboarding, saveOnboarding } from "../../lib/storage/onboarding";
import { listPublished, type Draft } from "../../lib/storage/drafts";
import { getProfile, profileWithDefaults } from "../../lib/profile";
import { defaultSiteUrl } from "../../lib/handle";
import { wordCount } from "../../lib/post";
import { registerDiscovery, unregisterDiscovery, getCreatorStats, type CreatorStats } from "../../lib/indexer-client";

const CF_ANALYTICS_URL = "https://dash.cloudflare.com/?to=/:account/pages/view/bka-public/analytics";

export function StatsTab() {
  const user = useAuthStore((s) => s.user)!;
  const [loading, setLoading] = useState(true);
  const [handle, setHandle] = useState<string | null>(null);
  const [siteUrl, setSiteUrl] = useState<string | null>(null);
  const [published, setPublished] = useState<Draft[]>([]);
  const [registered, setRegistered] = useState(false);
  const [indexed, setIndexed] = useState<CreatorStats | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const hydrate = useCallback(async () => {
    setLoading(true);
    const onb = await getOnboarding(user.sub);
    const pub = await listPublished(user.sub);
    const h = onb?.handle ?? null;
    const url = onb?.cf_pages_project || (h ? defaultSiteUrl(h) : null);
    const reg = !!onb?.indexer_registered;
    setHandle(h); setSiteUrl(url); setPublished(pub); setRegistered(reg);
    if (h && reg) { try { setIndexed(await getCreatorStats(h)); } catch { setIndexed(null); } }
    else setIndexed(null);
    setLoading(false);
  }, [user.sub]);

  useEffect(() => { void hydrate(); }, [hydrate]);

  const totalWords = published.reduce((n, d) => n + wordCount(d.body), 0);
  const lastPub = published[0];
  const profile = profileWithDefaults(getProfile(user.sub));

  const toggleDiscovery = async () => {
    if (!handle || !siteUrl) { setError("Finish onboarding before joining discovery."); return; }
    const credential = user.credential;
    setBusy(true); setError(null);
    try {
      if (registered) {
        await unregisterDiscovery(credential);
        await saveOnboarding(user.sub, { indexer_registered: 0 });
      } else {
        await registerDiscovery(credential, handle, `${siteUrl}/feed.json`, siteUrl);
        await saveOnboarding(user.sub, { indexer_registered: 1 });
      }
      await hydrate();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="st-loading">loading your stats…<S /></div>;

  return (
    <div className="st-root">
      <div className="st-col">
        <div className="st-head">
          <h1 className="st-title">Stats</h1>
          <button className="st-refresh" onClick={() => void hydrate()}>refresh</button>
        </div>

        <div className="st-cards">
          {/* Site overview */}
          <section className="st-card">
            <div className="st-card-h">Your site</div>
            {siteUrl
              ? <a className="st-site" href={siteUrl} target="_blank" rel="noreferrer">{siteUrl.replace("https://", "")} ↗</a>
              : <div className="st-muted">not set up yet</div>}
            <div className="st-metrics">
              <div className="st-metric"><span className="st-num">{published.length}</span><span className="st-cap">published</span></div>
              <div className="st-metric"><span className="st-num">{totalWords.toLocaleString()}</span><span className="st-cap">words</span></div>
              <div className="st-metric"><span className="st-num">{profile.theme}</span><span className="st-cap">theme</span></div>
            </div>
            {lastPub && <div className="st-muted">last published {new Date(lastPub.published_at ?? lastPub.updated_at).toISOString().slice(0, 10)}</div>}
          </section>

          {/* Discovery */}
          <section className="st-card">
            <div className="st-card-h">Discovery</div>
            <p className="st-muted">
              List your site in BKA discovery so readers can find you when they ask "what should I
              see?" Opt-in, metadata only, no tracking — leave any time.
            </p>
            <div className="st-disc">
              <span className={"st-badge " + (registered ? "st-badge-on" : "")}>{registered ? "listed" : "not listed"}</span>
              {registered && indexed && <span className="st-muted">{indexed.items.length} item{indexed.items.length === 1 ? "" : "s"} indexed</span>}
              <button className="st-toggle" disabled={busy} onClick={() => void toggleDiscovery()}>
                {busy ? "…" : registered ? "leave discovery" : "list my site"}
              </button>
            </div>
          </section>

          {/* Recent publishes */}
          <section className="st-card">
            <div className="st-card-h">Recent publishes</div>
            {published.length === 0
              ? <div className="st-muted">Nothing published yet.</div>
              : <ul className="st-pubs">
                  {published.slice(0, 8).map((d) => (
                    <li key={d.id} className="st-pub">
                      <span className="st-pub-title">{d.title.trim() || "Untitled"}</span>
                      <span className="st-pub-date">{new Date(d.published_at ?? d.updated_at).toISOString().slice(0, 10)}</span>
                      {siteUrl && <a className="st-pub-view" href={`${siteUrl}/posts/${d.published_slug}`} target="_blank" rel="noreferrer">view ↗</a>}
                    </li>
                  ))}
                </ul>}
          </section>

          {/* Page views — honest about the limitation */}
          <section className="st-card">
            <div className="st-card-h">Page views</div>
            <p className="st-muted">
              Your traffic lives in <em>your</em> Cloudflare account. BKA can't read it from the
              browser (Cloudflare's API isn't reachable here), and we won't route your CF token
              through a server — so view it where it lives, or add your own analytics to your site.
            </p>
            <a className="st-cf" href={CF_ANALYTICS_URL} target="_blank" rel="noreferrer">open Cloudflare analytics ↗</a>
          </section>
        </div>

        {error && <div className="st-error">{error}</div>}
      </div>
      <S />
    </div>
  );
}

function S() {
  return (
    <style>{`
      .st-loading { height: 100%; display: flex; align-items: center; justify-content: center; font-family: var(--font-mono); font-size: 12px; color: var(--ink-faint); font-style: italic; }
      .st-root { height: 100%; overflow: auto; display: flex; justify-content: center; padding: var(--space-6) var(--space-5); }
      .st-col { width: min(720px, 100%); }
      .st-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: var(--space-5); }
      .st-title { font-family: var(--font-serif); font-size: 30px; font-weight: 600; margin: 0; color: var(--ink); }
      .st-refresh { font-family: var(--font-mono); font-size: 11px; color: var(--ink-soft); background: var(--paper-sunk); border: 1px solid var(--line); border-radius: 999px; padding: 5px 14px; cursor: pointer; }
      .st-refresh:hover { color: var(--teal); border-color: var(--teal); }
      .st-cards { display: grid; grid-template-columns: 1fr 1fr; gap: var(--space-4); }
      .st-card { background: var(--paper-raised); border: 1px solid var(--line); border-radius: var(--radius-2); padding: var(--space-4); }
      .st-card-h { font-family: var(--font-mono); font-size: 11px; text-transform: uppercase; letter-spacing: 0.06em; color: var(--ink-faint); margin-bottom: var(--space-3); }
      .st-site { font-family: var(--font-mono); font-size: 14px; color: var(--teal); display: inline-block; margin-bottom: var(--space-3); }
      .st-metrics { display: flex; gap: var(--space-5); margin: var(--space-3) 0; }
      .st-metric { display: flex; flex-direction: column; }
      .st-num { font-family: var(--font-serif); font-size: 26px; font-weight: 600; color: var(--ink); }
      .st-cap { font-size: 11px; color: var(--ink-faint); text-transform: uppercase; letter-spacing: 0.04em; }
      .st-muted { font-size: 13px; line-height: 1.55; color: var(--ink-soft); }
      .st-muted em { font-style: italic; color: var(--ink); }
      .st-disc { display: flex; align-items: center; gap: var(--space-3); margin-top: var(--space-3); flex-wrap: wrap; }
      .st-badge { font-family: var(--font-mono); font-size: 10px; text-transform: uppercase; letter-spacing: 0.06em; padding: 3px 10px; border-radius: 999px; background: var(--paper-sunk); color: var(--ink-faint); }
      .st-badge-on { background: var(--teal-dim); color: var(--teal); }
      .st-toggle { margin-left: auto; font-family: var(--font-sans); font-size: 12px; font-weight: 600; color: var(--teal); background: var(--teal-dim); border: none; border-radius: var(--radius-1); padding: 6px 14px; cursor: pointer; }
      .st-toggle:disabled { opacity: 0.5; cursor: not-allowed; }
      .st-pubs { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: var(--space-2); }
      .st-pub { display: flex; align-items: center; gap: var(--space-2); }
      .st-pub-title { flex: 1; min-width: 0; font-size: 13px; color: var(--ink); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .st-pub-date { font-family: var(--font-mono); font-size: 11px; color: var(--ink-faint); }
      .st-pub-view { font-size: 12px; }
      .st-cf { display: inline-block; margin-top: var(--space-2); font-size: 13px; color: var(--teal); }
      .st-error { margin-top: var(--space-4); padding: var(--space-3); background: var(--rust-bg); border: 1px solid rgba(180,80,42,0.3); border-radius: var(--radius-1); color: var(--rust); font-size: 13px; user-select: text; }
      @media (max-width: 720px) { .st-cards { grid-template-columns: 1fr; } }
    `}</style>
  );
}
